import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Field, LangText, ImageField } from "@/pages/AdminCMS";

const empty = { slug: "", title: {}, excerpt: {}, body: {}, cover_image: "", published: false };

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [status, setStatus] = useState("");

  const load = () => api.get("/admin/posts").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing };
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    if (editing.id) await api.put(`/admin/posts/${editing.id}`, payload);
    else await api.post("/admin/posts", payload);
    setEditing(null);
    setStatus("Saved"); setTimeout(() => setStatus(""), 2500);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/posts/${id}`);
    load();
  };

  if (!posts) return <p className="p-8 text-sm text-[#8A8378]">Loading…</p>;

  return (
    <div className="bg-white border border-[#191713]/10 border-t-0 p-5 lg:p-8" data-testid="admin-blog">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#8A8378]">Articles marked "Published" appear on the public /blog page. The Blog link only shows once at least one article is published.</p>
        <div className="flex items-center gap-3">
          {status && <span className="text-xs text-emerald-700" data-testid="blog-status">{status}</span>}
          <button data-testid="blog-new-post" onClick={() => setEditing({ ...empty })} className="btn-gold !py-2 !px-4 text-xs">
            <Plus size={13} /> New article
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-6 border border-[#A6853B]/40 p-5" data-testid="blog-editor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="URL slug (auto from title if empty)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
            <label className="text-xs flex items-center gap-2 mt-6">
              <input type="checkbox" data-testid="blog-published-toggle" checked={!!editing.published}
                onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published (visible on the site)
            </label>
          </div>
          <LangText label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
          <LangText label="Excerpt (shown in the list & SEO description)" area value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} />
          <LangText label="Body (paragraphs separated by a blank line)" area value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} />
          <ImageField label="Cover image (optional)" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} />
          <div className="flex gap-3 mt-4">
            <button data-testid="blog-save" onClick={save} className="btn-gold !py-2 !px-4 text-xs"><Save size={13} /> Save article</button>
            <button onClick={() => setEditing(null)} className="btn-outline-dark !py-2 !px-4 text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-6 divide-y divide-[#191713]/8">
        {posts.length === 0 && <p className="py-6 text-sm text-[#8A8378]">No articles yet.</p>}
        {posts.map((p) => (
          <div key={p.id} className="py-4 flex items-center justify-between gap-4" data-testid={`blog-row-${p.slug}`}>
            <div>
              <p className="text-sm font-medium text-[#191713]">
                {p.title?.EN || p.title?.FR || p.slug}
                <span className={`ml-3 text-[0.6rem] tracking-wide uppercase px-2 py-0.5 ${p.published ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  {p.published ? "Published" : "Draft"}
                </span>
              </p>
              <p className="text-xs text-[#8A8378] mt-0.5">/blog/{p.slug} · {new Date(p.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`blog-edit-${p.slug}`} onClick={() => setEditing(p)} className="btn-outline-dark !py-1.5 !px-3 text-xs">Edit</button>
              <button data-testid={`blog-delete-${p.slug}`} onClick={() => remove(p.id)} aria-label="Delete"
                className="p-2 border border-red-300 text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
